import { PropsWithChildren, FC } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Tabs } from 'antd'
import {
    getReferencesOfJobRequestAttachments,
    successContentForMessages,
} from '@OpsQuality/logic'
import Messages from '@OpsQuality/components/MessagesUI/Messages'
import { getJobRequestDetails } from '@OpsQuality/JobRequestDetails/query'
import styles from '@OpsQuality/styles.css'

import Box from '~/core/Box'
import { OPS_QUALITY_PATH } from '~/redux-modules/preferences'
import PartsTab from '../PartsTab'
import { adminJobRequestQuery_jobRequest as JobRequestType } from '../__generated__/adminJobRequestQuery'
import { FILES, MESSAGES, PARTS, PHOTOS } from '../constants'

import {
    determineTab,
    useDigitalFictiv,
    useJobRequestLinesByPart,
    useProvisionedAttachmentDataForTabs,
} from './logic'
import OverviewTab from './OverviewTab'
import FilesLayout from './FilesTab/FilesLayout'
import PhotosLayout from './PhotosTab/PhotosLayout'
import { ParamsType } from './types'

interface DetailsContainerProps {
    jobRequest?: JobRequestType | null
    isMobile: boolean
}

const DetailsContainer: FC<DetailsContainerProps> = props => {
    const { jobRequest, isMobile } = props
    const { tab } = useParams<ParamsType>()
    const navigate = useNavigate()
    const activeTab = determineTab(tab)
    const jobRequestLinesByPart = useJobRequestLinesByPart(
        jobRequest?.parts || [],
    )
    const jobRequestLinesWithAttachmentRef =
        getReferencesOfJobRequestAttachments(jobRequestLinesByPart)
    const jobRequestLineAttchments = useProvisionedAttachmentDataForTabs(
        jobRequestLinesWithAttachmentRef,
    )
    const { images } = jobRequestLineAttchments
    const partCount = jobRequest?.parts?.length || 0
    const messageCount = jobRequest?.jobRequestComments?.length || 0
    const filesData = useDigitalFictiv(
        jobRequestLinesWithAttachmentRef,
        jobRequestLineAttchments?.files,
        jobRequest?.id,
    )

    const onTabClick = (nextTab: string) => {
        if (jobRequest) {
            const { id } = jobRequest

            return navigate(
                `${OPS_QUALITY_PATH}/${id}/${nextTab.toLowerCase()}`,
                {
                    replace: true,
                },
            )
        }
        return null
    }

    const MessagesTab: FC<PropsWithChildren<unknown>> = () => {
        const itemNames = jobRequestLinesWithAttachmentRef.map(
            (jobRequestLine: { name: string }) => jobRequestLine.name,
        )

        return (
            <Messages
                jobRequestId={jobRequest?.id}
                messages={jobRequest?.jobRequestComments}
                subjectTypes={jobRequest?.subjectTypes}
                itemPartNames={itemNames}
                refetchQuery={getJobRequestDetails}
                successContent={successContentForMessages}
            />
        )
    }

    return (
        <Box direction="col" style={{ height: '100%' }}>
            <OverviewTab jobRequest={jobRequest} />
            <Box direction="col" className={styles.tabsDetailPage}>
                <Tabs defaultActiveKey={activeTab} onTabClick={onTabClick}>
                    <Tabs.TabPane tab={`PARTS (${partCount})`} key={PARTS}>
                        {jobRequest && <PartsTab jobRequest={jobRequest} />}
                    </Tabs.TabPane>
                    <Tabs.TabPane
                        tab={`MESSAGES (${messageCount})`}
                        key={MESSAGES}
                    >
                        {<MessagesTab />}
                    </Tabs.TabPane>
                    <Tabs.TabPane
                        tab={`PHOTOS (${images.length})`}
                        key={PHOTOS}
                    >
                        <PhotosLayout photos={images} isMobile={isMobile} />
                    </Tabs.TabPane>
                    <Tabs.TabPane
                        tab={`FILES (${filesData.length})`}
                        key={FILES}
                    >
                        <FilesLayout files={filesData} isMobile={isMobile} />
                    </Tabs.TabPane>
                </Tabs>
            </Box>
        </Box>
    )
}

export default DetailsContainer
